// components/ValidationBDC.tsx
import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Modal, Space, Spin, Table, Tabs, Upload } from "antd";
import type { TabsProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { UserOutlined, UploadOutlined, FilePdfOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import {
  Typography, Box,
} from "@mui/material";
import axios from "axios";
import { IBonDeCaisse, IParametreBDC } from "../../../dataTypes/GlobalDataType";
import { Crypter } from "../../../Functions/Security/Crypter";
import Data from '../../../Data/Data.json';
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from "../../../Models/Utilisateur";
import { fetchDataTable2, GlobalExportToCSV, GlobalExportToExcel, GlobalExportToJSON, GlobalExportToPDF, GlobalExportToWord } from "../../../utilitaires/globalFunction";
import formatNumber from "../../../Functions/Traitement/FormatNumber";
import SweetAlert from "sweetalert2";
import ParamsPicklist from "../../../Types/ParamsPicklist";
import InputForm from "../../InputT";
import SearchInput from "../../SearchInput";
import ExportMenu from "../../ExportMenu";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function ValidationBDC() {
  const auth = useAuthUser()
  const authHeader = useAuthHeader()
  const Perss: Utilisateur = JSON.parse(JSON.stringify(auth()))

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<IBonDeCaisse | null>(null);
  const [paramBDC, setParamBDC] = useState<IParametreBDC[]>([]);

  // Boutons (même pilotage que /prets/simulation)
  const [enregActve, setEnregActve] = useState(true)
  const [echActive, setEchActive] = useState(true)
  const [impActive, setImpActive] = useState(true)
  const [annulActive, setAnnulActive] = useState(true)
  const [consulActive, setConsulActive] = useState(false)
  const [contratActif, setContratActif] = useState(true)
  const [cartonOk, setCartonOk] = useState(false)
  const [cartonNom, setCartonNom] = useState<string>("")
  const [cartonPreview, setCartonPreview] = useState(false)
  // Contrat sélectionné localement : NON enregistré tant que « Valider »
  // n'est pas confirmé (upload différé au clic sur Valider)
  const [pendingContrat, setPendingContrat] = useState<{ base64: string; name: string } | null>(null)
  // Aperçu avant validation (même mécanisme que l'enregistrement de /prets/demande)
  const [previewOpen, setPreviewOpen] = useState(false)
  const pendingValues = useRef<IBonDeCaisse | null>(null)

  const [echeancier, setEcheancier] = useState<{
    periode: number;
    dateImput: string;
    interet: number;
    taxe: number;
    netAPayer: number;
  }[]>([]);

  // Bons de caisse en attente de validation uniquement (statut 01)
  let searchBdc: ParamsPicklist = { CodeAgence: Perss.codage, Table: "BONDECAISSE", Colonne1: "NUMBDC", Colonne2: "NOMCLI", Colagence: 'CODAGE', Choix: 22, colfiltre: "CDETAT", valfiltre: "01" }

  const columns: ColumnsType<any> = [
    { title: 'Période', dataIndex: 'periode', key: 'periode', width: 80 },
    { title: 'Date imputation', dataIndex: 'dateImput', key: 'dateImput', width: 120 },
    { title: 'Intérêt par période', dataIndex: 'interet', key: 'interet', align: 'right', render: (v: number) => formatNumber(Number(v)) },
    { title: 'Taxe par période', dataIndex: 'taxe', key: 'taxe', align: 'right', render: (v: number) => formatNumber(Number(v)) },
    { title: 'Net par période', dataIndex: 'netAPayer', key: 'netAPayer', align: 'right', render: (v: number) => formatNumber(Number(v)) },
  ];

  // ------------------------------------------------------------------
  // Pilotage des boutons
  // ------------------------------------------------------------------
  function initialiazeButton() {
    setEnregActve(true)
    setEchActive(true)
    setImpActive(true)
    setAnnulActive(true)
    setConsulActive(false)
    setContratActif(true)
    setCartonOk(false)
  }

  function handleAnulle() {
    setEcheancier([])
    setSelected(null)
    setPendingContrat(null)
    setPreviewOpen(false)
    pendingValues.current = null
    form.resetFields()
    initialiazeButton()
  }

  function cunsultButton() {
    setEchActive(false)
    setImpActive(false)
    setAnnulActive(false)
    setConsulActive(false)
    setContratActif(false)   // zone contrat activée
    // Valider activé uniquement si le contrat signé est chargé (cartonOk)
  }

  // ------------------------------------------------------------------
  // Chargement
  // ------------------------------------------------------------------
  const fetchData = async (): Promise<IBonDeCaisse[]> => {
    try {
      var params = { CodeAgence: Perss.codage, Table: "BONDECAISSE", Colonne1: "NUMCLI", Colonne2: "NOMCLI", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "CDETAT", valfiltre: "01" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      return res || []
    } catch (err) {
      console.error("Erreur de chargement:", err);
      return []
    }
  };

  const fetchDataTypeBDC = async () => {
    try {
      var params = { CodeAgence: Perss.codage, Table: "PARAMBDC", Colonne1: "CODBDC", Colonne2: "LIBELL", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      if (res.length !== 0) {
        setParamBDC(res);
      }
    } catch (err) {
      console.error("Erreur de chargement:", err);
    }
  };

  const fmtDate = (d: any) => d ? new Date(d).toISOString().substring(0, 10) : "";

  // Disponible d'un compte via GetCompte (comme BlocageComptes)
  const fetchDispon = async (numcpt: any, dispo: string) => {
    if (!numcpt) {
      form.setFieldValue(dispo, "");
      return;
    }
    try {
      const crypted = Crypter.encrypt(String(numcpt));
      const response = await axios({
        method: 'get',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.GetCompte,
        headers: {
          'CypherNumCpte': crypted,
          'Authorization': authHeader(),
          'Content-Type': 'application/json'
        }
      });
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data));
        if (res.StatutCode == 200 && res.Result?.Table?.[0] != null) {
          form.setFieldValue(dispo, formatNumber(Number(res.Result.Table[0].DISPON) || 0));
        }
      }
    } catch (error) {
      console.log("Erreur GetCompte:", error);
    }
  }

  // Aperçu frais de souscription (même mécanisme que Safe Care)
  const fetchFrais = async (typBdc?: string, montant?: any) => {
    if (!typBdc || !Number(montant)) {
      form.setFieldValue('FRAISSOUSCR', '');
      return;
    }
    try {
      const response = await axios({
        method: 'get',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.ApercuFraisBDC,
        headers: {
          'CypherTypBdc': Crypter.encrypt(JSON.stringify(typBdc)),
          'CypherAgence': Crypter.encrypt(JSON.stringify(Perss.codage)),
          'CypherMontant': Crypter.encrypt(JSON.stringify(Number(montant))),
          'Authorization': authHeader(),
          'Content-Type': 'application/json'
        }
      });
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data));
        const status = res?.Result?.Table?.[0];
        const detail = res?.Result?.Table1?.[0];
        if (String(status?.Status) === '200') {
          const frais = Number(detail?.Frais) || 0;
          form.setFieldValue('FRAISSOUSCR', frais > 0 ? formatNumber(frais) : 'Aucun');
        } else {
          form.setFieldValue('FRAISSOUSCR', status?.result || 'Indisponible');
        }
      }
    } catch (err) {
      console.warn('ApercuFrais - réponse inattendue', err);
      form.setFieldValue('FRAISSOUSCR', 'Indisponible');
    }
  };

  // Libellé du code opération du bon (OPERAT.LIBELL)
  const fetchLibOpe = async (codope?: string) => {
    if (!codope) { form.setFieldValue('LIBOPE', ''); return; }
    try {
      var params = { CodeAgence: '', Table: "OPERAT", Colonne1: "CODOPE", Colonne2: "LIBELL", Colagence: '', Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      const row = (res || []).find((o: any) => o.CODOPE === codope);
      form.setFieldValue('LIBOPE', row?.LIBELL || '');
    } catch (err) {
      console.warn('fetchLibOpe - erreur', err);
    }
  };

  // ------------------------------------------------------------------
  // Contrat signé : lecture / upload (obligatoire avant validation)
  // ------------------------------------------------------------------
  const fetchContrat = async (numbdc: string) => {
    try {
      const data = Crypter.encrypt(JSON.stringify({ NUMBDC: numbdc, CODAGE: Perss.codage }));
      const response = await axios({
        method: 'get',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.GetContratBDC,
        headers: {
          'CypherBonDeCaisse': data,
          'Authorization': authHeader(),
          'Content-Type': 'application/json'
        }
      });
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data));
        const doc = res?.Result?.Table?.[0];
        if (doc?.PIECEJOINTE) {
          form.setFieldValue('CARTONBDC', doc.PIECEJOINTE);
          setCartonNom(doc.NOMFICHIER || 'contrat.pdf');
          setCartonOk(true);
          setPendingContrat(null);
          return;
        }
      }
      setCartonOk(false);
      setPendingContrat(null);
    } catch (err) {
      console.warn('GetContratBDC - erreur', err);
      setCartonOk(false);
      setPendingContrat(null);
    }
  };

  const fileToDataUri = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });

  // Upload direct dans le formulaire : base64 -> API -> stockage (même
  // mécanisme que « Justificatif document » de /indemnisation/creation)
  const handleUploadCarton = async (file: File) => {
    if (!selected?.NUMBDC) {
      SweetAlert.fire({ title: 'Chargez d\'abord un bon de caisse (Consulter).', icon: 'warning' });
      return;
    }
    if (file.size / 1024 > 3000) {
      SweetAlert.fire({ title: 'Fichier trop volumineux', text: `"${file.name}" dépasse 3 Mo.`, icon: 'warning' });
      return;
    }
    const base64 = await fileToDataUri(file);
    form.setFieldValue('CARTONBDC', base64);
    setCartonNom(file.name);
    // Le fichier n'est PAS envoyé au serveur ici — il est conservé en
    // local et n'est enregistré qu'au clic sur « Valider ».
    setPendingContrat({ base64, name: file.name });
    setCartonOk(true);
  };

  const uploadContrat = async (base64: string, nomFichier?: string): Promise<boolean> => {
    const numbdc = form.getFieldValue('NUMBDC');
    if (!numbdc || !base64) return false;
    try {
      const payload = { NUMBDC: numbdc, CODAGE: Perss.codage, UTISAI: Perss.coduti, NOMFICHIER: nomFichier || 'contrat_' + numbdc + '.pdf', PIECEJOINTE: base64 };
      const data = Crypter.encrypt(JSON.stringify(payload));
      const response = await axios({
        method: 'post',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.UploadContratBDC,
        headers: {
          'Authorization': authHeader(),
          'Content-Type': 'application/json'
        },
        data: JSON.stringify(data)   // payload chiffré dans le BODY (trop gros pour un header)
      });
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data));
        if (res.StatutCode == 200) {
          setPendingContrat(null);
          return true;
        }
        SweetAlert.fire({ title: "Upload du contrat", text: res.Message, icon: "warning" });
        return false;
      }
      return false;
    } catch (err: any) {
      console.warn('UploadContratBDC - erreur', err);
      SweetAlert.fire({ title: "Upload du contrat", text: "Echec d'enregistrement du contrat : " + (err?.message || err), icon: "error" });
      return false;
    }
  };

  // ------------------------------------------------------------------
  // Export contrat : client + données du bon + zone signature/acceptation
  // ------------------------------------------------------------------
  const genererContrat = () => {
    if (!selected) {
      SweetAlert.fire({ title: "Chargez d'abord un bon de caisse (Consulter).", icon: "warning" })
      return;
    }
    const v = (f: string) => (form.getFieldValue(f) ?? selected?.[f as keyof IBonDeCaisse] ?? '');
    // jsPDF ne rend pas les espaces insécables → les remplace par des
    // espaces normales (sinon « 2 /000 /000 » s'affiche avec des « / »)
    const fmtAmt = (x: any) => String(x ?? '').replace(/[  ]/g, ' ');
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const M = 50;
    let y = 60;

    doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text("CONTRAT DE BON DE CAISSE", W / 2, y, { align: 'center' });
    y += 18; doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text("AnyBank — Souscription d'un bon de caisse", W / 2, y, { align: 'center' });
    y += 30;

    doc.setFont('helvetica', 'bold'); doc.text("INFORMATIONS CLIENT", M, y); y += 8;
    autoTable(doc, {
      startY: y, margin: { left: M, right: M }, styles: { fontSize: 8 },
      head: [['Indice client', 'Nom client', 'Agence']],
      body: [[v('NUMCLI'), v('lib_NUMCLI') || v('NOMCLI'), Perss.codage]],
    });
    y = (doc as any).lastAutoTable.finalY + 16;

    doc.setFont('helvetica', 'bold'); doc.text("CARACTÉRISTIQUES DU BON DE CAISSE", M, y); y += 8;
    autoTable(doc, {
      startY: y, margin: { left: M, right: M }, styles: { fontSize: 8 },
      head: [['N° BDC', 'Référence', 'Type', 'Imputation', 'Fréquence', 'Durée']],
      body: [[v('NUMBDC'), v('REFBDC'), v('TYPBDC'), v('TYPIMPUTINT'), v('FREQPROV'), String(v('DUREE'))]],
    });
    y = (doc as any).lastAutoTable.finalY + 8;
    autoTable(doc, {
      startY: y, margin: { left: M, right: M }, styles: { fontSize: 8 },
      head: [['Montant', 'Date début', 'Date fin', 'Taux intérêt', 'Taux taxe']],
      body: [[fmtAmt(formatNumber(Number(v('MONTANT')) || 0)), fmtDate(v('DATDEBUT')), fmtDate(v('DATFIN')), String(v('TXINT')), String(v('TXTAXE'))]],
    });
    y = (doc as any).lastAutoTable.finalY + 16;

    doc.setFont('helvetica', 'bold'); doc.text("INTÉRÊTS ET TAXES", M, y); y += 8;
    autoTable(doc, {
      startY: y, margin: { left: M, right: M }, styles: { fontSize: 8 },
      head: [['Intérêts bruts', 'Taxe retenue', 'Net versé au client', 'Frais souscription']],
      body: [[fmtAmt(form.getFieldValue('TotInt') || ''), fmtAmt(form.getFieldValue('TotTaxe') || ''), fmtAmt(form.getFieldValue('TotNet') || ''), fmtAmt(form.getFieldValue('FRAISSOUSCR') || '')]],
    });
    y = (doc as any).lastAutoTable.finalY + 16;

    doc.setFont('helvetica', 'bold'); doc.text("COMPTES CONCERNÉS", M, y); y += 8;
    autoTable(doc, {
      startY: y, margin: { left: M, right: M }, styles: { fontSize: 8 },
      head: [['Compte dépôt', 'Compte BDC', 'Compte intérêts']],
      body: [[v('CPTDEPOT'), v('CPTBDC'), v('CPTINTCLI')]],
    });
    y = (doc as any).lastAutoTable.finalY + 30;

    // Zone d'acceptation / signature
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    doc.text("Je soussigné(e), " + (v('lib_NUMCLI') || v('NOMCLI') || '________________'), M, y); y += 14;
    doc.text("déclare avoir pris connaissance des conditions du présent bon de caisse et les accepter.", M, y); y += 26;
    doc.setDrawColor(0); doc.rect(M, y, 200, 60);
    doc.text("Signature du client", M + 8, y + 14);
    doc.rect(W - M - 200, y, 200, 60);
    doc.text("Cachet / Visa de la banque", W - M - 192, y + 14);
    y += 80;
    doc.setFontSize(7);
    doc.text("Document généré le " + new Date().toLocaleDateString() + " — AnyBank", M, y);

    doc.save("Contrat_BDC_" + (v('NUMBDC') || 'export') + ".pdf");
  };

  // ------------------------------------------------------------------
  // Échéancier (aperçu — imputation réelle à la dotation EOD)
  // ------------------------------------------------------------------
  const joursFreq = (freq: string) => {
    switch ((freq || "").toUpperCase()) {
      case "A": return 360;
      case "S": return 180;
      case "T": return 90;
      case "M": return 30;
      case "B": return 15;
      case "F": return 60;
      case "H": return 7;
      case "J": return 1;
      default: return 30;
    }
  }

  function calculer(source?: any) {
    const get = (f: string) => source ? source[f] : form.getFieldValue(f);

    const freq = (get("FREQPROV") || "").toUpperCase();
    const montant = Number(get("MONTANT")) || 0;
    const tauxInteret = Number(get("TXINT")) || 0;
    const tauxTaxe = Number(get("TXTAXE")) || 0;
    const duree = Number(get("DUREE")) || 0;
    const datDebut = get("DATDEBUT") || Perss.datjour;

    if (!freq || !montant || !tauxInteret || !duree) {
      SweetAlert.fire({
        icon: "warning",
        title: "Champs manquants",
        text: "Veuillez renseigner la fréquence, le montant, le taux et la durée.",
        target: document.body,
        backdrop: true,
      });
      return;
    }

    const prochaineDate = new Date(datDebut);
    const jours = joursFreq(freq);
    prochaineDate.setDate(prochaineDate.getDate() + jours * duree);
    const formattedDate = prochaineDate.toISOString().split("T")[0]

    let montIntBrut = (montant * tauxInteret * duree * jours) / 36000;
    let montProvFreq = Math.round(montIntBrut / duree);
    let montInt = montProvFreq * duree;
    let montTaxeBrut = (montInt * tauxTaxe) / 100;
    let montTaxeFreq = Math.round(montTaxeBrut / duree);
    let montTaxe = montTaxeFreq * duree;
    let montNet = montInt - montTaxe;

    let dateEcheance = new Date(datDebut);
    const newEcheancier = [];
    for (let i = 1; i <= duree; i++) {
      const prochaine = new Date(dateEcheance);
      prochaine.setDate(prochaine.getDate() + jours);
      newEcheancier.push({
        key: i,
        periode: i,
        dateImput: dateEcheance.toLocaleDateString(),
        interet: montProvFreq,
        taxe: montTaxeFreq,
        netAPayer: montProvFreq - montTaxeFreq,
      });
      dateEcheance = prochaine;
    }
    setEcheancier(newEcheancier);

    form.setFieldsValue({
      MTNINT: montInt || 0,
      MTNTAXE: montTaxe || 0,
      MTNTAXEFREQ: montTaxeFreq || 0,
      MTNNETAPAYER: montNet || 0,
      MTNPROVFREQ: montProvFreq || 0,
      DATFIN: formattedDate,
      TotInt: formatNumber(montInt),
      TotTaxe: formatNumber(montTaxe),
      TotNet: formatNumber(montNet),
    });
  }

  // ------------------------------------------------------------------
  // Consultation : charge le BDC (statut 01) dont le numéro est saisi
  // ------------------------------------------------------------------
  const consult = async () => {
    const numbdc = form.getFieldValue("NUMBDC")
    if (!numbdc) {
      SweetAlert.fire({ title: "Sélectionnez ou saisissez d'abord un numéro de bon de caisse.", icon: "warning" })
      return
    }
    setLoading(true)
    const list = await fetchData()
    setLoading(false)
    const row = list.find(c => c.NUMBDC === numbdc)
    if (!row) {
      SweetAlert.fire({ title: "Bon de caisse introuvable", text: "Aucun bon en attente de validation pour : " + numbdc, icon: "warning" })
      return
    }

    const cleanRow: any = {
      ...row,
      DATDEBUT: fmtDate(row.DATDEBUT),
      DATFIN: fmtDate(row.DATFIN),
      lib_NUMCLI: row.NOMCLI,
      TotInt: formatNumber(Number(row.MTNINT)),
      TotTaxe: formatNumber(Number(row.MTNTAXE)),
      TotNet: formatNumber(Number(row.MTNNETAPAYER)),
    }
    setSelected(row);
    form.setFieldsValue(cleanRow);
    fetchDispon(row.CPTDEPOT, 'DISPONCPTDEPOT')
    fetchDispon(row.CPTBDC, 'DISPONCPTBDC')
    fetchDispon(row.CPTINTCLI, 'DISPONCPTINT')
    calculer({ ...row })
    fetchFrais(row.TYPBDC, row.MONTANT)
    fetchLibOpe(row.CODOPE)
    fetchContrat(row.NUMBDC || '')
    cunsultButton()
  }

  useEffect(() => {
    fetchDataTypeBDC()
  }, []);

  // ------------------------------------------------------------------
  // Exports de l'échéancier
  // ------------------------------------------------------------------
  const handleExport = (format: any) => {
    const title = "Exportation tableau imputation interets " + (selected?.NUMBDC || form.getFieldValue("NUMBDC") || "");
    switch (format) {
      case 'excel':
        GlobalExportToExcel(echeancier, title, "TableauInteretsBDC")
        break;
      case 'pdf':
        GlobalExportToPDF(echeancier, title, "TableauInteretsBDC")
        break;
      case 'word':
        GlobalExportToWord(echeancier, title, "TableauInteretsBDC")
        break;
      case 'csv':
        GlobalExportToCSV(echeancier, title, "TableauInteretsBDC")
        break;
      case 'json':
        GlobalExportToJSON(echeancier, title, "TableauInteretsBDC")
        break;
      default:
        break;
    }
  };

  // ------------------------------------------------------------------
  // Validation du BDC (statut 01 -> 02 + écritures comptables)
  // ------------------------------------------------------------------
  const onFinish = async () => {
    // (true) : inclut les valeurs du store sans Form.Item (MTNINT, MTNTAXE, ...)
    const values = form.getFieldsValue(true);
    const valuesBdc: IBonDeCaisse = { ...values }
    valuesBdc.NOMCLI = values.lib_NUMCLI || values.NOMCLI
    valuesBdc.CODAGE = Perss.codage
    valuesBdc.UTIVAL = Perss.coduti
    valuesBdc.DATCRE = values.DATCRE || new Date()
    valuesBdc.DATVAL = new Date()
    valuesBdc.HEUREVAL = new Date().toISOString()
    valuesBdc.CDETAT = "02"
    valuesBdc.CODOPE = values.CODOPE || "990"
    valuesBdc.PENALITE = 0;
    valuesBdc.INTACQUIS = 0;
    valuesBdc.INTDUS = 0;
    Object.keys(valuesBdc).forEach(k => { if (k.startsWith('lib_') || k.startsWith('Tot') || k.startsWith('DISPON') || k === 'FRAISSOUSCR' || k === 'CARTONBDC' || k === 'CODEBDC') delete (valuesBdc as any)[k] })
    // coercition numérique des champs calculés non montés
    valuesBdc.IDBDC = Number(valuesBdc.IDBDC) || 0;
    valuesBdc.DUREE = Number(valuesBdc.DUREE) || 0;
    valuesBdc.MONTANT = Number(valuesBdc.MONTANT) || 0;
    valuesBdc.TXINT = Number(valuesBdc.TXINT) || 0;
    valuesBdc.TXTAXE = Number(valuesBdc.TXTAXE) || 0;
    valuesBdc.MTNINT = Number(valuesBdc.MTNINT) || 0;
    valuesBdc.MTNTAXE = Number(valuesBdc.MTNTAXE) || 0;
    valuesBdc.MTNPROVFREQ = Number(valuesBdc.MTNPROVFREQ) || 0;
    valuesBdc.MTNTAXEFREQ = Number(valuesBdc.MTNTAXEFREQ) || 0;
    valuesBdc.MTNNETAPAYER = Number(valuesBdc.MTNNETAPAYER) || 0;

    if (!cartonOk) {
      SweetAlert.fire({
        icon: "warning",
        title: "Contrat signé requis",
        text: "Veuillez charger le contrat signé par le client (PDF) avant de valider.",
        target: document.body,
        backdrop: true,
      });
      return;
    }

    if (!selected || selected.CDETAT !== '01') {
      SweetAlert.fire({
        icon: "warning",
        title: "Validation bon de caisse",
        text: "Impossible de valider ce bon de caisse : il a déjà fait l'objet d'une validation ou d'un rejet.",
        target: document.body,
        backdrop: true,
      });
      return;
    }

    // Aperçu avant validation (comme l'enregistrement de /prets/demande) :
    // la validation n'est exécutée qu'après « Confirmer et valider ».
    pendingValues.current = valuesBdc;
    setPreviewOpen(true);
  };

  // Clic sur « Confirmer et valider » dans le modal d'aperçu : upload du
  // contrat en attente (si nouveau) puis appel de l'API de validation.
  const confirmerValidation = async () => {
    const valuesBdc = pendingValues.current;
    if (!valuesBdc) return;
    setPreviewOpen(false);
    pendingValues.current = null;
    setLoading(true);

    // Le contrat signé n'est enregistré en base qu'ici, à la validation
    if (pendingContrat) {
      const ok = await uploadContrat(pendingContrat.base64, pendingContrat.name);
      if (!ok) {
        setLoading(false);
        SweetAlert.fire({
          title: "Validation annulée",
          text: "Le contrat signé n'a pas pu être enregistré : le bon n'a pas été validé.",
          icon: "error",
          target: document.body,
        });
        return;
      }
    }

    let data = Crypter.encrypt(JSON.stringify(valuesBdc));
    let config = {
      method: 'put',
      maxBodyLength: Infinity,
      url: Data.ApiUrl.ValidateBonDeCaisse,
      headers: {
        'CypherBonDeCaisse': data,
        'Authorization': authHeader(),
        'Content-Type': 'application/json'
      },
      data: data
    };

    axios.request(config)
      .then((response) => {
        setLoading(false);
        if (response.data) {
          const res = JSON.parse(Crypter.decrypt(response.data));
          if (res.StatutCode == 200) {
            handleAnulle();
            SweetAlert.fire({ title: res.Message, text: "Code : " + res.Result, icon: "success" })
          }
          else {
            SweetAlert.fire({ title: "Warning", text: res.Message, icon: "warning" })
          }
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error)
        SweetAlert.fire({ title: "Error Front", text: error, icon: "error" })
      });
  };

  const onFinishFailed = async (errorInfo: any) => {
    var errors: any = [];
    errorInfo.errorFields.map((error: any) => {
      errors.push(error.name + "");
    })
    SweetAlert.fire({ title: "Veuillez remplir tous les champs obligatoires", text: errors.toString().replaceAll(',', " - "), icon: 'warning', confirmButtonText: 'OK' })
  };

  // ------------------------------------------------------------------
  // Onglet validation (même layout que /bondecaisse/creation)
  // ------------------------------------------------------------------
  const items: TabsProps['items'] = [
    {
      key: '1',
      label: (
        <span id='Tab'>
          <UserOutlined />
          Validation bon de caisse
        </span>
      ),
      children: (
        <div className='tabItem'>
          <div >
            <Box fontStyle="inherit" className='border' width={"85%"} position={"absolute"}>
              <Box display={"flex"} fontStyle="inherit" className='border-left'>

                <Box fontStyle="inherit" className='border-left'>
                  <SearchInput dissabledCode={!consulActive && !annulActive} notRequired notremove enable width={7} form={form} Titre='Recherche Bon de caisse' label='N° BDC' name='NUMBDC' Parametre={searchBdc} />
                  <InputForm width={11} label='Référence BDC' name='REFBDC' type='label' notRequired />
                  <InputForm width={11} label='Code Simul' name='CODSIMUL' type='label' notRequired />
                  <InputForm width={11} label='Indice client' name='NUMCLI' type='label' notRequired />
                  <InputForm width={11} label='Nom client' name='lib_NUMCLI' type='label' notRequired />

                  {/* Contrat signé — même présentation que « Justificatif
                      document » de /indemnisation/creation */}
                  <Form.Item label="Contrat signé (PDF) *" required style={{ marginBottom: 0 }}>
                    <Upload.Dragger
                      accept=".pdf"
                      showUploadList={false}
                      disabled={contratActif}
                      beforeUpload={(file) => { handleUploadCarton(file); return false; }}
                      style={{ padding: 4 }}
                    >
                      <div style={{ padding: '2px 0' }}>
                        <UploadOutlined style={{ fontSize: 22, color: '#116599' }} />
                        <div style={{ fontSize: 12, marginTop: 4, color: '#8c8c8c' }}>
                          Uploadez le contrat signé (PDF — max 3 Mo)
                        </div>
                      </div>
                    </Upload.Dragger>
                  </Form.Item>
                </Box>

                <Box fontStyle="inherit" className='border-left'>
                  <InputForm width={11} label='Type BDC' name='TYPBDC' type='label' notRequired />
                  <InputForm width={11} label='Type imputation' name='TYPIMPUTINT' type='label' notRequired />
                  <InputForm width={11} label='Fréquence' name='FREQPROV' type='label' notRequired />
                  <InputForm width={11} label='Durée' name='DUREE' type='label' notRequired />

                  {/* Vignette du contrat chargé : œil -> visualisation */}
                  {cartonOk && (
                    <div style={{ width: 92, textAlign: 'center', position: 'relative', marginTop: 8 }}>
                      <div
                        onClick={() => setCartonPreview(true)}
                        style={{ width: 92, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, border: '1px solid #d9d9d9', background: '#fff5f5', cursor: 'pointer' }}
                      >
                        <FilePdfOutlined style={{ fontSize: 28, color: '#d4380d' }} />
                        <EyeOutlined style={{ position: 'absolute', top: 4, left: 4, color: 'var(--darkColor, #116599)', fontSize: 12 }} />
                      </div>
                      <div style={{ fontSize: 10, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={cartonNom}>
                        {cartonNom}
                      </div>
                      <DeleteOutlined
                        onClick={() => { form.setFieldValue('CARTONBDC', undefined); setCartonOk(false); setCartonNom(''); }}
                        style={{ position: 'absolute', top: 2, right: 2, color: '#ff4d4f', background: '#fff', borderRadius: '50%', padding: 2, cursor: 'pointer' }}
                      />
                    </div>
                  )}
                  {!cartonOk && !contratActif && (
                    <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 6 }}>
                      Le contrat signé est obligatoire pour valider
                    </div>
                  )}
                </Box>

                <Box fontStyle="inherit" className='border-left'>
                  <InputForm width={11} label='Montant' name='MONTANT' type='label' notRequired />
                  <InputForm width={11} label='Date début' name='DATDEBUT' type='label' notRequired />
                  <InputForm width={11} label='Date fin' name='DATFIN' type='label' notRequired />
                  <InputForm width={11} label='Taux intérêt' name='TXINT' type='label' notRequired />
                  <InputForm width={11} label='Taux taxe' name='TXTAXE' type='label' notRequired />
                </Box>

                <Box fontStyle="inherit" className='border-left'>
                  <InputForm width={11} label='Compte dépôt' name='CPTDEPOT' type='label' notRequired />
                  <InputForm width={11} label='Disponible' name='DISPONCPTDEPOT' type='label' notRequired />
                  <InputForm width={11} label='Compte BDC' name='CPTBDC' type='label' notRequired />
                  <InputForm width={11} label='Disponible' name='DISPONCPTBDC' type='label' notRequired />
                  <InputForm width={11} label='Compte intérêts' name='CPTINTCLI' type='label' notRequired />
                  <InputForm width={11} label='Disponible' name='DISPONCPTINT' type='label' notRequired />
                </Box>

                <Box fontStyle="inherit" className='border-left'>
                  <InputForm width={11} label='Code opération' name='CODOPE' type='label' notRequired />
                  <InputForm width={11} label='Libellé opération' name='LIBOPE' type='label' notRequired />
                  <InputForm width={11} label='Intérêts bruts' name='MTNINT' type='label' notRequired />
                  <InputForm width={11} label='Taxe retenue' name='MTNTAXE' type='label' notRequired />
                  <InputForm width={11} label='Net à payer' name='MTNNETAPAYER' type='label' notRequired />
                </Box>

              </Box>
            </Box>
          </div>
        </div>
      ),
    }
  ];

  return (
    <div className='tab'>
      <Typography align='center' className='formTitle'><b>VALIDATION BON DE CAISSE</b></Typography>
      <Spin spinning={loading} size='large' style={{ color: '#116599' }} >
        <Form autoComplete="off" labelAlign='left' form={form} name="dynamic_form_complex" onFinish={onFinish} onFinishFailed={onFinishFailed} style={{ minWidth: 'auto', maxWidth: 'auto' }}>

          <div className='bG'>
            <legend><Typography color={"#116599"}>Actions</Typography></legend>
            <Form.Item shouldUpdate className='bouttong'>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size='large' className='bt_add' disabled={consulActive} onClick={consult} type="ghost" htmlType="button" block  >
                  Consulter
                </Button>
                <Button size='large' className='bt_add' disabled={echActive} type="ghost" onClick={() => calculer()} block>
                  Échéancier
                </Button>
                <ExportMenu disabled={impActive} onExport={handleExport} />
                <Button size='large' className='bt_add' onClick={genererContrat} disabled={contratActif} type="ghost" htmlType="button" block  >
                  Contrat
                </Button>
                <Button size='large' className='bt_add' disabled={!cartonOk} type="ghost" htmlType="submit" block>
                  Valider
                </Button>
                <Button size='large' disabled={annulActive} onClick={handleAnulle} className='bt_add' type="ghost" htmlType="reset" block>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className='clientP'>
            <div >
              <Tabs size='small' type="card" defaultActiveKey="1" items={items} />
              <Box fontStyle="inherit" className='tabElement' style={{ top: '63%', height: 'auto' }}>
                <Spin spinning={loading}>
                  <Table size='small' style={{ width: 'auto' }} columns={columns} dataSource={echeancier} scroll={{ y: 200, x: 10 }} pagination={false} />
                </Spin>
                <Box display={"flex"} fontStyle="inherit" className='border-left bordure1' style={{ marginTop: 8 }}>
                  <InputForm notRequired disabled width={8} label='Intérêts bruts' name='TotInt' type='text' />
                  <InputForm notRequired disabled width={8} label='Taxe retenue' name='TotTaxe' type='text' />
                  <InputForm notRequired disabled width={8} label='Net versé au client' name='TotNet' type='text' />
                  <InputForm notRequired disabled width={8} label='Frais souscription' name='FRAISSOUSCR' type='text' />
                </Box>
              </Box>
            </div>
            <div>
            </div>
          </div>
        </Form>
      </Spin>

      {/* Prévisualisation du contrat signé — même style que l'indemnisation */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: 'var(--darkblue)' }}>
            <EyeOutlined style={{ color: 'var(--darkColor)' }} />
            {cartonNom || 'Aperçu du contrat'}
          </div>
        }
        open={cartonPreview}
        onCancel={() => setCartonPreview(false)}
        footer={null}
        width="90%"
        centered
        zIndex={2100}
        style={{ maxWidth: '1400px' }}
        bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflow: 'auto', padding: 16 }}
      >
        {form.getFieldValue('CARTONBDC') && (
          <iframe
            src={form.getFieldValue('CARTONBDC')}
            title="Aperçu contrat"
            style={{ width: '100%', height: '75vh', border: '1px solid var(--colorOpaque030)', borderRadius: 10 }}
          />
        )}
      </Modal>

      {/* Aperçu avant validation — même mécanisme que l'enregistrement de
          /prets/demande : récapitulatif du bon + échéancier, puis
          « Confirmer et valider » exécute l'upload du contrat et la
          validation. Rien n'est persisté avant ce clic. */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: 'var(--darkblue)' }}>
            <EyeOutlined style={{ color: 'var(--darkColor)' }} />
            Aperçu — validation du bon de caisse {pendingValues.current?.NUMBDC}
          </div>
        }
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        width="90%"
        centered
        zIndex={2050}
        style={{ maxWidth: '1400px' }}
        footer={[
          <Button key="cancel" onClick={() => setPreviewOpen(false)}>Annuler</Button>,
          <Button key="ok" type="primary" onClick={confirmerValidation} style={{ background: '#116599' }}>
            Confirmer et valider
          </Button>
        ]}
        bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflow: 'auto', padding: 16 }}
      >
        {pendingValues.current && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px 16px', marginBottom: 12, fontSize: 13 }}>
              <div><b>N° BDC :</b> {pendingValues.current.NUMBDC}</div>
              <div><b>Client :</b> {pendingValues.current.NOMCLI} ({pendingValues.current.NUMCLI})</div>
              <div><b>Type :</b> {pendingValues.current.TYPBDC} — {pendingValues.current.TYPIMPUTINT === 'PRE' ? 'Précompté' : 'Postcompté'}</div>
              <div><b>Montant :</b> {formatNumber(Number(pendingValues.current.MONTANT))}</div>
              <div><b>Durée :</b> {pendingValues.current.DUREE} × {pendingValues.current.FREQPROV}</div>
              <div><b>Période :</b> {fmtDate(pendingValues.current.DATDEBUT)} → {fmtDate(pendingValues.current.DATFIN)}</div>
              <div><b>Taux intérêt :</b> {pendingValues.current.TXINT} %</div>
              <div><b>Intérêts bruts :</b> {formatNumber(Number(pendingValues.current.MTNINT))}</div>
              <div><b>Taxe retenue :</b> {formatNumber(Number(pendingValues.current.MTNTAXE))}</div>
              <div><b>Net versé au client :</b> {formatNumber(Number(pendingValues.current.MTNNETAPAYER))}</div>
              <div><b>Compte dépôt :</b> {pendingValues.current.CPTDEPOT}</div>
              <div><b>Compte intérêts :</b> {pendingValues.current.CPTINTCLI}</div>
            </div>
            <Table
              size='small'
              columns={columns}
              dataSource={echeancier}
              scroll={{ y: 420 }}
              pagination={false}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              {pendingContrat
                ? `Contrat signé : ${pendingContrat.name} — sera enregistré à la confirmation`
                : `Contrat signé : ${cartonNom || 'déjà enregistré'}`}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
